import React from 'react';

type ProgressEvent = {
  type?: string;
  status?: string;
  state?: string;
  tool?: string;
  stage?: string;
  detail?: string;
  filePath?: string;
  taskId?: string;
  role?: string;
  feature?: string;
  completedTaskId?: string;
  targets?: { cloud?: string[]; public?: string[]; data?: string[] };
  changed?: { cloud?: string[]; public?: string[]; data?: string[] };
  changes?: { path: string; type: string }[];
};

/**
 * AgentProgressTicker - Shows what the agent is doing right now.
 *
 * Simple: wrench icon + action + filename badge + typing dots.
 * No roles, no states, no phases. Just what's happening.
 */
export default function AgentProgressTicker({
  event,
}: {
  event?: ProgressEvent | null;
}) {
  const stage = (event?.stage || '').trim();
  const filePath = (event?.filePath || '').trim();
  const filename = filePath ? filePath.split('/').pop() || '' : '';

  // Extract action verb from stage (e.g., "Writing index.html" → "Writing")
  const action = stage && filename && stage.includes(filename)
    ? stage.replace(filename, '').trim()
    : stage || 'Working...';

  const hasTool = Boolean(event?.tool);

  return (
    <div
      className="mt-4 flex items-center gap-3"
      aria-live="polite"
      aria-label="Progress"
    >
      {/* Wrench icon (only when a tool is active) */}
      {hasTool && (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className="h-5 w-5 flex-shrink-0 text-[#3b82f6]"
          aria-label="Tool"
        >
          <path
            d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}

      {/* Action + filename */}
      <div className="min-w-0 flex-1 flex items-center gap-2">
        <span className="text-[14px] leading-5 text-[#e5e7eb] font-medium whitespace-nowrap">
          {action}
        </span>
        {filename && (
          <span className="px-2 py-0.5 rounded bg-white/10 text-[13px] text-[#9ca3af] font-mono truncate">
            {filename}
          </span>
        )}
      </div>

      {/* Typing dots */}
      <div className="flex items-center gap-1 flex-shrink-0" aria-hidden="true">
        <span
          className="h-2 w-2 rounded-full bg-[#3b82f6]"
          style={{ animation: 'typing-bounce 1.4s ease-in-out infinite', animationDelay: '0ms' }}
        />
        <span
          className="h-2 w-2 rounded-full bg-[#3b82f6]"
          style={{ animation: 'typing-bounce 1.4s ease-in-out infinite', animationDelay: '200ms' }}
        />
        <span
          className="h-2 w-2 rounded-full bg-[#3b82f6]"
          style={{ animation: 'typing-bounce 1.4s ease-in-out infinite', animationDelay: '400ms' }}
        />
      </div>

      <style>{`
        @keyframes typing-bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
      `}</style>
    </div>
  );
}
